import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Search } from 'lucide-react';
import CustomModal from '../CustomModal';
import Row from '../../../components/Row';
import Column from '../../../components/Column';
import Text from '../../../components/Text';
import { styles } from '../../../styles/themes/style';
import DropdownMenu from '../../../components/dropdown/DropdownMenu';
import DropdownItem from '../../../components/dropdown/DropdownItem';
import { ICampaign } from '../../../interfaces/models/campaign';
import { IConnection } from '../../../interfaces/models/connection';
import Button from '../../Button';
import { useAppHandlers } from '../../../services/actions/app/useAppHandlers';
import { useConnectionsHandlers } from '../../../services/actions/connections/useConnectionsHandlers';
import { useAuthStore } from '../../../stores/auth/useAuthStore';

interface IAddToCampaignModalProps {
  selectedConnections?: IConnection[];
  selectedAllConnections?: boolean;
  total?: number;
  onClose?: () => void;
}

const AddToCampaignModal: FunctionComponent<IAddToCampaignModalProps> = ({
  selectedConnections = [],
  selectedAllConnections = false,
  total = 0,
  onClose,
}) => {
  const { hideModal, showErrorToast } = useAppHandlers();
  const {
    getDraftCampaignsHandler,
    addConnectionsToCampaignHandler,
    getSelectedConnections,
  } = useConnectionsHandlers();
  const { store: { user } } = useAuthStore();

  const [campaigns, setCampaigns] = useState<ICampaign[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Handlers from useConnectionsHandlers are recreated every render — keep a ref
  // so the initial fetch effect doesn't re-run in an infinite loop.
  const getDraftCampaignsHandlerRef = useRef(getDraftCampaignsHandler);
  getDraftCampaignsHandlerRef.current = getDraftCampaignsHandler;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const list = await getDraftCampaignsHandlerRef.current();
      if (cancelled) return;
      setCampaigns(list);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user?._id]);

  // Fresh lookup on every open, with the cursor already in the search field.
  useEffect(() => {
    if (!isDropdownOpen) {
      setSearch('');
      return;
    }
    const frame = requestAnimationFrame(() => searchInputRef.current?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(frame);
  }, [isDropdownOpen]);

  const filteredCampaigns = useMemo(() => {
    const normalize = (s: string) =>
      (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    const q = normalize(search.trim());
    if (!q) return campaigns;
    return campaigns.filter(c => normalize(c.name).includes(q));
  }, [campaigns, search]);

  // Items stop propagation, which also skips react-bootstrap's close-on-select,
  // so close the menu here.
  const handleCampaignSelect = useCallback((campaignId: string) => {
    setSelectedCampaignId(campaignId);
    setIsDropdownOpen(false);
  }, []);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || !filteredCampaigns.length) return;
    e.preventDefault();
    handleCampaignSelect(filteredCampaigns[0]._id);
  };

  const resolveConnectionIds = async (): Promise<string[]> => {
    if (selectedAllConnections && total > 0) {
      const rows = await getSelectedConnections(total, true, false);
      return (rows ?? []).map((c: IConnection) => c._id);
    }
    return selectedConnections.map(c => c._id);
  };

  const handleSubmit = async () => {
    if (!selectedCampaignId) {
      showErrorToast({ message: 'Please select a campaign.' });
      return;
    }

    setSubmitting(true);
    try {
      const connectionIds = await resolveConnectionIds();
      await addConnectionsToCampaignHandler(selectedCampaignId, connectionIds);
      hideModal();
      onClose?.();
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCampaignName =
    !selectedCampaignId
      ? 'Choose campaign'
      : campaigns.find(c => c._id === selectedCampaignId)?.name || 'Choose campaign';

  const selectedCampaignButton = (
    <Container $hasValue={!!selectedCampaignId}>
      {selectedCampaignName}
    </Container>
  );

  return (
    <CustomModal
      headingText="Add to campaign"
      descriptionText="Choose a campaign to add contacts as leads. Running campaigns start reaching out to them right away."
      closeButton
      bodyPadding="0 24px 0px 24px;"
      onCancelHandler={() => {
        hideModal();
        onClose?.();
      }}
    >
      <Column gap="24px">
        <Column gap="8px">
          <StyledDropdownMenu
            selectStyle
            tooltipMessage={selectedCampaignName}
            title={selectedCampaignButton}
            show={isDropdownOpen}
            onToggle={setIsDropdownOpen}
            disabled={loading}
          >
            {loading ? (
              <EmptyContainer>
                <Text $description $faded>Loading campaigns…</Text>
              </EmptyContainer>
            ) : campaigns.length > 0 ? (
              <>
                <SearchWrapper>
                  <SearchIcon><Search size={14} strokeWidth={2} /></SearchIcon>
                  <SearchInput
                    ref={searchInputRef}
                    type="text"
                    value={search}
                    placeholder="Search campaigns…"
                    onChange={e => setSearch(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    onClick={e => e.stopPropagation()}
                  />
                </SearchWrapper>
                <OptionsWrapper>
                  {filteredCampaigns.length > 0 ? (
                    filteredCampaigns.map(campaign => (
                      <DropdownItem
                        key={campaign._id}
                        title={campaign.name}
                        onClickHandler={e => {
                          e.stopPropagation();
                          handleCampaignSelect(campaign._id);
                        }}
                        $disabled={false}
                      >
                        <Text>{campaign.name}</Text>
                      </DropdownItem>
                    ))
                  ) : (
                    <EmptyContainer>
                      <Text $description $faded>No campaigns match “{search.trim()}”.</Text>
                    </EmptyContainer>
                  )}
                </OptionsWrapper>
              </>
            ) : (
              <EmptyContainer>
                <Text $description $faded>
                  No campaigns yet. Create a campaign first.
                </Text>
              </EmptyContainer>
            )}
          </StyledDropdownMenu>
        </Column>

        <Row spaceBetween style={{ flexDirection: 'row-reverse' }}>
          <Button
            onClickHandler={handleSubmit}
            disabled={!selectedCampaignId || submitting || !campaigns.length}
            $type="blue"
            isLoading={submitting}
            $width="auto"
            $customDisabled={styles.colors.primary200}
          >
            {submitting ? 'Adding…' : 'Add to campaign'}
          </Button>
        </Row>
      </Column>
    </CustomModal>
  );
};

const Container = styled.div<{ $hasValue?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 34px;
  border: none;
  width: 100%;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  line-height: 20px;
  font-family: NunitoSans;
  font-weight: 400;
  color: ${({ $hasValue }) => $hasValue ? styles.colors.black600 : styles.colors.black300};
`;

const StyledDropdownMenu = styled(DropdownMenu)`
  border: none;
  .actions-dropdown + div {
    width: 100%;
    max-height: 350px;
    overflow: hidden !important;
    padding: 0;
  }
`;

const SearchWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid ${styles.colors.black200};
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 22px;
  display: inline-flex;
  align-items: center;
  pointer-events: none;
  color: ${styles.colors.black300};
`;

const SearchInput = styled.input`
  width: 100%;
  height: 34px;
  padding: 0 10px 0 30px;
  border: 1px solid ${styles.colors.black200};
  border-radius: 8px;
  outline: none;
  font-family: NunitoSans;
  font-size: 14px;
  color: ${styles.colors.black600};
  background: ${styles.colors.white};

  &::placeholder { color: ${styles.colors.black300}; }
  &:hover { border-color: ${styles.colors.black300}; }
  &:focus { border-color: ${styles.colors.primary600}; }
`;

// The panel itself clips at 350px; the list scrolls inside it under the search field.
const OptionsWrapper = styled.div`
  max-height: 290px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 6px 0;

  .dropdown-item > * {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const EmptyContainer = styled.div`
  padding: 16px 12px;
  text-align: center;
  color: ${styles.colors.black400};
`;

export default AddToCampaignModal;
